/**
 * SpotlightCard — card with a soft radial highlight that tracks the mouse.
 * Same source / behavior as web/src/components/SpotlightCard.tsx, exposed
 * here so admin pages get the identical premium feel.
 *
 * Use sparingly: every interactive card pulling 3 redraws/frame is fine,
 * but a 30-row table cell wrapped per row tanks scroll perf. Reserve for
 * dashboard tiles, hero panels, and the like.
 */
import {
  useRef,
  useState,
  type MouseEventHandler,
  type PropsWithChildren,
} from 'react';

interface SpotlightCardProps extends PropsWithChildren {
  className?: string;
  /** rgba string — the spotlight tint color. Pick something matching the section accent. */
  spotlightColor?: `rgba(${number}, ${number}, ${number}, ${number})`;
  /** Opacity at peak — drops dramatically below 0.2 for large light surfaces. */
  intensity?: number;
}

export function SpotlightCard({
  children,
  className = '',
  spotlightColor = 'rgba(96, 165, 250, 0.18)',
  intensity = 0.55,
}: SpotlightCardProps): JSX.Element {
  const divRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [opacity, setOpacity] = useState(0);

  const handleMouseMove: MouseEventHandler<HTMLDivElement> = (e) => {
    if (divRef.current === null) return;
    const rect = divRef.current.getBoundingClientRect();
    setPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  return (
    <div
      ref={divRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={(): void => setOpacity(intensity)}
      onMouseLeave={(): void => setOpacity(0)}
      className={`group relative overflow-hidden ${className}`}
    >
      <div
        className="pointer-events-none absolute inset-0 transition-opacity duration-500"
        style={{
          opacity,
          background: `radial-gradient(circle at ${position.x}px ${position.y}px, ${spotlightColor}, transparent 60%)`,
        }}
      />
      <div className="relative">{children}</div>
    </div>
  );
}
